import React, { useState } from 'react';
import { _T, C, CSS } from '../../shared/theme';

const nl = (nlStr, enStr, lang) => lang === 'nl' ? nlStr : enStr;

// ─── Section data ─────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: 'intro',
    icon: '🏢',
    nl: 'Introductie',
    en: 'Introduction',
    content: {
      nl: [
        {
          type: 'h2',
          text: 'Welkom bij het Sense Lifting Business Dashboard',
        },
        {
          type: 'p',
          text: 'Het Sense Lifting Business Dashboard is meer dan een gewoon bedrijfsbeheertool. Het combineert professionele bedrijfsprocessen met wetenschappelijk onderbouwde gezondheids- en welzijnsdata van medewerkers. Het principe is eenvoudig: gezonde, energieke medewerkers presteren beter, verzuimen minder en dragen meer bij aan de groei van het bedrijf.',
        },
        {
          type: 'p',
          text: 'Dit dashboard is speciaal ontworpen voor eigenaren, managers en HR-professionals die begrijpen dat bedrijfssucces en menselijk welzijn onlosmakelijk met elkaar verbonden zijn.',
        },
        {
          type: 'h3',
          text: 'Kernprincipes',
        },
        {
          type: 'list',
          items: [
            '🔬 Wetenschap als basis — alle inzichten zijn gebaseerd op peer-reviewed onderzoek naar mentale energie, fysieke belasting en cognitieve prestaties.',
            '🔒 Privacy first — medewerkers beheren zelf welke gezondheidsdata ze delen. Niets wordt zonder toestemming zichtbaar.',
            '🤖 AI Coach — de ingebouwde energiecoach analyseert taken en tickets op basis van beschrijving en prioriteit en geeft een inschatting van de mentale en fysieke belasting.',
            '💼 Volledig geïntegreerd — van facturatie tot eigendomsoverdracht, van verlofbeheer tot CRM, alles in één platform.',
          ],
        },
      ],
      en: [
        {
          type: 'h2',
          text: 'Welcome to the Sense Lifting Business Dashboard',
        },
        {
          type: 'p',
          text: 'The Sense Lifting Business Dashboard is more than an ordinary business management tool. It combines professional business processes with scientifically-backed employee health and wellness data. The principle is simple: healthy, energized employees perform better, take fewer sick days and contribute more to company growth.',
        },
        {
          type: 'p',
          text: 'This dashboard is specially designed for owners, managers and HR professionals who understand that business success and human well-being are inextricably linked.',
        },
        {
          type: 'h3',
          text: 'Core Principles',
        },
        {
          type: 'list',
          items: [
            '🔬 Science as foundation — all insights are based on peer-reviewed research into mental energy, physical load and cognitive performance.',
            '🔒 Privacy first — employees control which health data they share. Nothing becomes visible without consent.',
            '🤖 AI Coach — the built-in energy coach analyzes tasks and tickets based on description and priority and estimates mental and physical load.',
            '💼 Fully integrated — from invoicing to ownership transfer, from leave management to CRM, everything in one platform.',
          ],
        },
      ],
    },
  },
  {
    id: 'navigation',
    icon: '🗺️',
    nl: 'Navigatie',
    en: 'Navigation',
    content: {
      nl: [
        { type: 'h2', text: 'Dashboard Navigatie' },
        { type: 'p', text: 'Het dashboard is ingedeeld in 8 hoofdsecties, bereikbaar via de bovenste navigatiebalk.' },
        {
          type: 'navTable',
          rows: [
            { icon: '🏠', name: 'Home', desc: 'Dashboard overzicht, team feed, agenda en bedrijfshandleiding.' },
            { icon: '🤝', name: 'Klanten', desc: 'CRM, boekingen, diensten, producten, bestellingen, API en widget.' },
            { icon: '👥', name: 'Team', desc: 'Teamleden, taken (met energiecoach), projecten en tickets.' },
            { icon: '💚', name: 'Welzijn', desc: "Wellnessprogramma's, trainingen en activiteitsoverzicht." },
            { icon: '📊', name: 'Inzichten', desc: 'Pulse, rapporten, ROI en RI&E/eNPS analyses.' },
            { icon: '📋', name: 'HR', desc: 'Contracten, verlof, ziekmeldingen, verlofsaldo en HR-rollen.' },
            { icon: '🧾', name: 'Admin', desc: 'Volledige boekhouding en eigendomsoverdracht.' },
            { icon: '⚙️', name: 'Meer', desc: 'Bedrijfsprofiel en instellingen.' },
          ],
        },
        { type: 'p', text: 'Elke sectie heeft sub-tabs die automatisch worden gefilterd op basis van uw rol (eigenaar, beheerder, manager, lid, HR, accountant, boekhouder).' },
      ],
      en: [
        { type: 'h2', text: 'Dashboard Navigation' },
        { type: 'p', text: 'The dashboard is divided into 8 main sections, accessible via the top navigation bar.' },
        {
          type: 'navTable',
          rows: [
            { icon: '🏠', name: 'Home', desc: 'Dashboard overview, team feed, agenda and business manual.' },
            { icon: '🤝', name: 'Clients', desc: 'CRM, bookings, services, products, orders, API and widget.' },
            { icon: '👥', name: 'Team', desc: 'Team members, tasks (with energy coach), projects and tickets.' },
            { icon: '💚', name: 'Wellness', desc: 'Wellness programs, training and activity overview.' },
            { icon: '📊', name: 'Insights', desc: 'Pulse, reports, ROI and RI&E/eNPS analysis.' },
            { icon: '📋', name: 'HR', desc: 'Contracts, leave, sick reports, leave balance and HR roles.' },
            { icon: '🧾', name: 'Admin', desc: 'Full bookkeeping and ownership transfer.' },
            { icon: '⚙️', name: 'More', desc: 'Business profile and settings.' },
          ],
        },
        { type: 'p', text: 'Each section has sub-tabs that are automatically filtered based on your role (owner, admin, manager, member, HR, accountant, bookkeeper).' },
      ],
    },
  },
  {
    id: 'energy',
    icon: '⚡',
    nl: 'Energiecoach',
    en: 'Energy Coach',
    content: {
      nl: [
        { type: 'h2', text: 'AI Energiecoach — Taken & Tickets' },
        {
          type: 'highlight',
          color: '#5B9CF6',
          text: 'De energiecoach is een unieke AI-functie die per taak of ticket inschat hoeveel mentale en fysieke energie het kost voor de toegewezen persoon.',
        },
        { type: 'h3', text: 'Hoe werkt het?' },
        {
          type: 'list',
          items: [
            '📋 De coach analyseert de taakomschrijving op trefwoorden (analyse, ontwerp, vergadering, bouwen, installeren, etc.)',
            '🎯 De prioriteit van de taak bepaalt de basisbelasting (Kritiek 35pt, Hoog 25pt, Middel 15pt, Laag 8pt)',
            '⏰ De deadline voegt druk toe: overdue +20pt, morgen +15pt, binnen 3 dagen +10pt',
            '📊 De huidige werkdruk van de medewerker wordt berekend op basis van alle actieve taken op zijn/haar naam',
            '⚠️ Als de totale belasting boven 75% (mentaal of fysiek) uitkomt, geeft de coach een waarschuwing',
          ],
        },
        { type: 'h3', text: 'De wetenschap achter energiebeheer' },
        {
          type: 'p',
          text: 'Mentale energie is een eindige hulpbron. Onderzoek van Roy Baumeister (Ego Depletion Theory, 1998) toont aan dat wilskracht en cognitieve prestaties afnemen na intensieve mentale taken. Daniel Kahneman (Thinking Fast and Slow, 2011) beschrijft het Systeem 2-denken — analytisch, langzaam, energievragend — als de bron die het snelst uitgeput raakt bij complexe werktaken.',
        },
        {
          type: 'p',
          text: 'Fysieke energie hangt direct samen met slaapkwaliteit, beweging en herstel. De Sense Lifting app verzamelt slaap- en activiteitsdata die — mits gedeeld door de medewerker — worden meegewogen in de belastingsinschatting.',
        },
        {
          type: 'highlight',
          color: '#C05050',
          text: '⚠️ Overbelasting: als een medewerker minder dan 15% mentale of fysieke energie over heeft, toont de coach een rode waarschuwing. Dit is het moment om een gesprek aan te gaan of taken te herverdelen.',
        },
        { type: 'h3', text: 'Gebruik de coach' },
        {
          type: 'list',
          items: [
            'Ga naar Team → Taken of Team → Projecten',
            'Open of bekijk een taak die aan iemand is toegewezen',
            'Klik op de ⚡ knop op de taakkaart',
            'De energieanalyse toont mentale en fysieke kosten en de resterende energie',
          ],
        },
      ],
      en: [
        { type: 'h2', text: 'AI Energy Coach — Tasks & Tickets' },
        {
          type: 'highlight',
          color: '#5B9CF6',
          text: 'The energy coach is a unique AI feature that estimates how much mental and physical energy each task or ticket costs for the assigned person.',
        },
        { type: 'h3', text: 'How does it work?' },
        {
          type: 'list',
          items: [
            '📋 The coach analyzes the task description for keywords (analyze, design, meeting, build, install, etc.)',
            '🎯 The task priority determines the base load (Critical 35pt, High 25pt, Medium 15pt, Low 8pt)',
            '⏰ The deadline adds pressure: overdue +20pt, tomorrow +15pt, within 3 days +10pt',
            '📊 The current workload of the employee is calculated based on all active tasks assigned to them',
            '⚠️ If the total load exceeds 75% (mental or physical), the coach issues a warning',
          ],
        },
        { type: 'h3', text: 'The science behind energy management' },
        {
          type: 'p',
          text: 'Mental energy is a finite resource. Research by Roy Baumeister (Ego Depletion Theory, 1998) shows that willpower and cognitive performance decline after intensive mental tasks. Daniel Kahneman (Thinking Fast and Slow, 2011) describes System 2 thinking — analytical, slow, energy-intensive — as the resource that depletes fastest during complex work tasks.',
        },
        {
          type: 'p',
          text: 'Physical energy is directly related to sleep quality, movement and recovery. The Sense Lifting app collects sleep and activity data that — when shared by the employee — can be factored into the load estimate.',
        },
        {
          type: 'highlight',
          color: '#C05050',
          text: '⚠️ Overload: if an employee has less than 15% mental or physical energy remaining, the coach shows a red warning. This is the time to have a conversation or redistribute tasks.',
        },
        { type: 'h3', text: 'Using the coach' },
        {
          type: 'list',
          items: [
            'Go to Team → Tasks or Team → Projects',
            'Open or view a task assigned to someone',
            'Click the ⚡ button on the task card',
            'The energy analysis shows mental and physical costs and remaining energy',
          ],
        },
      ],
    },
  },
  {
    id: 'health',
    icon: '💚',
    nl: 'Gezondheid & Welzijn',
    en: 'Health & Wellness',
    content: {
      nl: [
        { type: 'h2', text: 'De relatie tussen gezondheid en bedrijfsprestaties' },
        {
          type: 'p',
          text: 'Sense Lifting verbindt de persoonlijke gezondheidsapp van medewerkers met het bedrijfsdashboard. Medewerkers kunnen zelf kiezen welke data ze delen. Wat deelbaar is:',
        },
        {
          type: 'list',
          items: [
            '😴 Slaapkwaliteit en -duur (score 0-100)',
            '🏃 Dagelijkse activiteit en stappen',
            '💓 Hartslag variabiliteit (HRV) als stressmaatstaf',
            '🧠 Focusscore en cognitieve prestaties uit de Sense Lifting games',
            '😊 Stemming en emotioneel welzijn (ochtend check-in)',
          ],
        },
        { type: 'h3', text: 'Wetenschappelijke onderbouwing' },
        {
          type: 'scientificBlock',
          items: [
            {
              ref: 'Gallup (2023) — State of the Global Workplace',
              text: 'Werknemers met een hoge betrokkenheid zijn 23% productiever en verzuimen 81% minder dan niet-betrokken medewerkers.',
            },
            {
              ref: 'Harvard Business Review — The ROI of Employee Wellness',
              text: 'Elke euro geïnvesteerd in werknemersgezondheid levert gemiddeld €2,50–€6,00 terug door verminderd ziekteverzuim en hogere productiviteit.',
            },
            {
              ref: 'Karasek & Theorell — Healthy Work (1990)',
              text: 'De Demand-Control-Support theorie toont aan dat hoge taakeisen gecombineerd met lage autonomie en weinig sociale steun leiden tot burnout en cardiovasculaire aandoeningen.',
            },
            {
              ref: 'Walker, M. — Why We Sleep (2017)',
              text: 'Slaaptekort vermindert cognitieve prestaties equivalent aan alcoholintoxicatie. Slechts 6 uur slaap gedurende 10 dagen heeft hetzelfde effect als 24 uur wakker blijven.',
            },
          ],
        },
        { type: 'h3', text: 'Pulse — Wekelijkse welzijnsmeting' },
        {
          type: 'p',
          text: 'Elke week ontvangen medewerkers een anonieme Pulse-check van 5 vragen. De antwoorden worden geaggregeerd en getoond in Inzichten → Pulse. U ziet trends per team, afdeling of de gehele organisatie. Individuele antwoorden zijn nooit inzichtelijk voor managers — alleen geanonimiseerde groepsscore.',
        },
        { type: 'h3', text: 'eNPS — Employee Net Promoter Score' },
        {
          type: 'p',
          text: 'De eNPS stelt één cruciale vraag: "Hoe waarschijnlijk is het dat u Sense Lifting aanbeveelt als werkgever?" Scores van 0-6 zijn detractors, 7-8 passief, 9-10 promoters. eNPS = % promoters − % detractors. Gezonde organisaties scoren doorgaans boven de 30.',
        },
      ],
      en: [
        { type: 'h2', text: 'The relationship between health and business performance' },
        {
          type: 'p',
          text: 'Sense Lifting connects employees\' personal health app with the business dashboard. Employees can choose which data to share. What can be shared:',
        },
        {
          type: 'list',
          items: [
            '😴 Sleep quality and duration (score 0-100)',
            '🏃 Daily activity and steps',
            '💓 Heart rate variability (HRV) as stress indicator',
            '🧠 Focus score and cognitive performance from Sense Lifting games',
            '😊 Mood and emotional wellbeing (morning check-in)',
          ],
        },
        { type: 'h3', text: 'Scientific background' },
        {
          type: 'scientificBlock',
          items: [
            {
              ref: 'Gallup (2023) — State of the Global Workplace',
              text: 'Employees with high engagement are 23% more productive and take 81% fewer sick days than disengaged employees.',
            },
            {
              ref: 'Harvard Business Review — The ROI of Employee Wellness',
              text: 'Every euro invested in employee health returns an average of €2.50–€6.00 through reduced absenteeism and higher productivity.',
            },
            {
              ref: 'Karasek & Theorell — Healthy Work (1990)',
              text: 'The Demand-Control-Support theory shows that high job demands combined with low autonomy and little social support lead to burnout and cardiovascular disease.',
            },
            {
              ref: 'Walker, M. — Why We Sleep (2017)',
              text: 'Sleep deprivation reduces cognitive performance equivalent to alcohol intoxication. Just 6 hours of sleep for 10 days has the same effect as being awake for 24 hours.',
            },
          ],
        },
        { type: 'h3', text: 'Pulse — Weekly wellness measurement' },
        {
          type: 'p',
          text: 'Every week employees receive an anonymous Pulse check of 5 questions. Answers are aggregated and shown in Insights → Pulse. You can see trends by team, department or the entire organization. Individual answers are never visible to managers — only anonymized group scores.',
        },
        { type: 'h3', text: 'eNPS — Employee Net Promoter Score' },
        {
          type: 'p',
          text: 'The eNPS asks one crucial question: "How likely are you to recommend Sense Lifting as an employer?" Scores of 0-6 are detractors, 7-8 passive, 9-10 promoters. eNPS = % promoters − % detractors. Healthy organizations typically score above 30.',
        },
      ],
    },
  },
  {
    id: 'team',
    icon: '👥',
    nl: 'Teambeheer',
    en: 'Team Management',
    content: {
      nl: [
        { type: 'h2', text: 'Teambeheer' },
        { type: 'h3', text: 'Rollen & Rechten' },
        {
          type: 'roleTable',
          rows: [
            { role: 'Eigenaar', icon: '👑', desc: 'Volledige toegang, eigendomsoverdracht, alle admin-functies.' },
            { role: 'Beheerder', icon: '🔑', desc: 'Vrijwel volledige toegang, geen eigendomsoverdracht.' },
            { role: 'Manager', icon: '📊', desc: 'Team, taken, projecten, klanten, boekingen.' },
            { role: 'HR', icon: '📋', desc: 'HR-functies, verlof, contracten, ziekmeldingen.' },
            { role: 'Lid', icon: '👤', desc: 'Eigen taken, welzijn, agenda, feed.' },
            { role: 'Accountant', icon: '🧾', desc: 'Boekhouding, facturen, bestellingen.' },
            { role: 'Boekhouder', icon: '📒', desc: 'Volledige administratie.' },
          ],
        },
        { type: 'h3', text: 'Afdelingen' },
        {
          type: 'p',
          text: 'Maak afdelingen aan via Team → Team → afdelingsbeheer. Koppel medewerkers aan afdelingen voor gefilterde pulse-rapporten, verzuimstatistieken en teamoverzichten.',
        },
        { type: 'h3', text: 'Taken & Energiecoach' },
        {
          type: 'p',
          text: 'Taken zijn de bouwstenen van dagelijks werk. Elke taak heeft een prioriteit (Kritiek/Hoog/Middel/Laag), status (Te doen/In uitvoering/Klaar), deadline en een verantwoordelijke. De energiecoach analyseert elke taak automatisch. Zie sectie Energiecoach voor details.',
        },
        { type: 'h3', text: 'Projecten & Sprints' },
        {
          type: 'p',
          text: 'Projecten bevatten sprints (tijdsboksen) die weer user stories/tickets bevatten. Het bord-overzicht geeft een Kanban-weergave (Te Doen → Bezig → Review → Klaar). Het sprint-overzicht toont voortgang per sprint inclusief burndown. Tickets in projecten hebben ook de energiecoach ingebouwd.',
        },
      ],
      en: [
        { type: 'h2', text: 'Team Management' },
        { type: 'h3', text: 'Roles & Permissions' },
        {
          type: 'roleTable',
          rows: [
            { role: 'Owner', icon: '👑', desc: 'Full access, ownership transfer, all admin functions.' },
            { role: 'Admin', icon: '🔑', desc: 'Near-full access, no ownership transfer.' },
            { role: 'Manager', icon: '📊', desc: 'Team, tasks, projects, customers, bookings.' },
            { role: 'HR', icon: '📋', desc: 'HR functions, leave, contracts, sick reports.' },
            { role: 'Member', icon: '👤', desc: 'Own tasks, wellness, agenda, feed.' },
            { role: 'Accountant', icon: '🧾', desc: 'Bookkeeping, invoices, orders.' },
            { role: 'Bookkeeper', icon: '📒', desc: 'Full administration.' },
          ],
        },
        { type: 'h3', text: 'Departments' },
        {
          type: 'p',
          text: 'Create departments via Team → Team → department management. Link employees to departments for filtered pulse reports, absence statistics and team overviews.',
        },
        { type: 'h3', text: 'Tasks & Energy Coach' },
        {
          type: 'p',
          text: 'Tasks are the building blocks of daily work. Each task has a priority (Critical/High/Medium/Low), status (To Do/In Progress/Done), deadline and a responsible person. The energy coach analyzes each task automatically. See the Energy Coach section for details.',
        },
        { type: 'h3', text: 'Projects & Sprints' },
        {
          type: 'p',
          text: 'Projects contain sprints (time boxes) which in turn contain user stories/tickets. The board view provides a Kanban layout (To Do → In Progress → Review → Done). The sprint view shows progress per sprint including burndown. Tickets in projects also have the energy coach built in.',
        },
      ],
    },
  },
  {
    id: 'admin',
    icon: '🧾',
    nl: 'Administratie',
    en: 'Administration',
    content: {
      nl: [
        { type: 'h2', text: 'Boekhouding & Administratie' },
        { type: 'p', text: 'De administratiemodule biedt een volledig dubbel boekhoudingssysteem. Bereikbaar via Admin → Administratie.' },
        {
          type: 'featureList',
          items: [
            {
              icon: '📄',
              title: 'Facturen',
              desc: 'Maak professionele facturen met eigen logo, kleur en layout. Zoek klanten op vanuit het CRM. Bereken BTW automatisch. Print of stuur per e-mail. Facturen worden automatisch verwerkt in de grootboekhouding.',
            },
            {
              icon: '🧾',
              title: 'Inkoopfacturen',
              desc: 'Leg leveranciersfacturen vast met leveranciersnaam, bedrag, vervaldatum en bijlage. Volg de betaalstatus (openstaand/betaald/achterstallig).',
            },
            {
              icon: '💸',
              title: 'Onkosten',
              desc: 'Registreer bedrijfsonkosten per categorie (kantoor, reis, horeca, etc.). Voeg bonnen bij. Bekijk per categorie totalen in een staafdiagram.',
            },
            {
              icon: '📚',
              title: 'Grootboek',
              desc: 'Dubbelboekhouding met debet/credit kolommen. Automatisch gegenereerde journaalposten vanuit facturen. Balans (activa/passiva) en winst-en-verliesrekening per periode. Printbaar.',
            },
            {
              icon: '🔗',
              title: 'Koppelingen',
              desc: 'Verbind met Moneybird, SnelStart, QuickBooks of Exact Online via API-sleutel of OAuth. Synchroniseer facturen en journaalposten automatisch.',
            },
          ],
        },
      ],
      en: [
        { type: 'h2', text: 'Bookkeeping & Administration' },
        { type: 'p', text: 'The administration module provides a complete double-entry bookkeeping system. Accessible via Admin → Administration.' },
        {
          type: 'featureList',
          items: [
            {
              icon: '📄',
              title: 'Invoices',
              desc: 'Create professional invoices with your own logo, color and layout. Search customers from the CRM. Calculate VAT automatically. Print or send by email. Invoices are automatically processed in the general ledger.',
            },
            {
              icon: '🧾',
              title: 'Bills',
              desc: 'Record supplier invoices with supplier name, amount, due date and attachment. Track payment status (open/paid/overdue).',
            },
            {
              icon: '💸',
              title: 'Expenses',
              desc: 'Record business expenses by category (office, travel, hospitality, etc.). Attach receipts. View category totals in a bar chart.',
            },
            {
              icon: '📚',
              title: 'General Ledger',
              desc: 'Double-entry bookkeeping with debit/credit columns. Automatically generated journal entries from invoices. Balance sheet (assets/liabilities) and profit & loss by period. Printable.',
            },
            {
              icon: '🔗',
              title: 'Connections',
              desc: 'Connect with Moneybird, SnelStart, QuickBooks or Exact Online via API key or OAuth. Synchronize invoices and journal entries automatically.',
            },
          ],
        },
      ],
    },
  },
  {
    id: 'transfer',
    icon: '🔄',
    nl: 'Eigendomsoverdracht',
    en: 'Ownership Transfer',
    content: {
      nl: [
        { type: 'h2', text: 'Eigendomsoverdracht via Stripe' },
        {
          type: 'highlight',
          color: '#9B30FF',
          text: '⚠️ Alleen de huidige eigenaar heeft toegang tot de overdrachtsmodule. Bereikbaar via Admin → Overdracht.',
        },
        { type: 'h3', text: 'Stap 1 — Partijen & Prijs' },
        {
          type: 'list',
          items: [
            'Zoek de nieuwe eigenaar(s) op via naam of e-mailadres',
            'Nodig externe partijen uit via e-mail als ze nog geen account hebben',
            'Stel de totale overdrachtssom en valuta in',
            'Kies het aantal termijnen (1/2/3/4/6/12) en de betaalfrequentie (maandelijks/kwartaal)',
            'De commissie van 8% wordt automatisch berekend over de totale som',
            'De eerste termijn moet groter zijn dan de 8% commissie',
          ],
        },
        { type: 'h3', text: 'Stap 2 — Contract' },
        {
          type: 'list',
          items: [
            'Het systeem genereert automatisch een overdrachtscontract',
            'Het contract bevat alle partijen, de prijs, het betalingsschema en de commissiestructuur',
            'U kunt het contract opnieuw genereren of afdrukken',
            'Bewaar het contract voor uw eigen administratie — het is niet rechtsgeldig zonder handtekeningen',
          ],
        },
        { type: 'h3', text: 'Stap 3 — Stripe Betaling' },
        {
          type: 'list',
          items: [
            'De verkoper doorloopt Stripe Express onboarding (eenmalig, ~5 min)',
            'Stripe verifieert de identiteit en bankgegevens van de verkoper — Sense Lifting ziet deze gegevens nooit',
            'De koper betaalt elke termijn via Stripe Checkout (veilige betaalpagina)',
            'Bij de eerste betaling houdt Sense Lifting automatisch 8% van de totale som als commissie',
            'De verkoper ontvangt het resterende bedrag van de eerste termijn minus de commissie',
            'Overige termijnen worden volledig uitbetaald aan de verkoper',
          ],
        },
        {
          type: 'highlight',
          color: '#4AC87A',
          text: '✅ Na succesvolle eerste betaling wordt de eigendomsoverdracht automatisch geregistreerd in het systeem. Het eigenaar-ID wordt bijgewerkt en er wordt een journaalpost aangemaakt.',
        },
        { type: 'h3', text: 'Commissieberekening' },
        {
          type: 'p',
          text: 'Commissie = 8% × totale overdrachtssom. De commissie wordt eenmalig ingehouden op de eerste termijn. Voorbeeld: Totaalprijs €100.000, 4 termijnen van €25.000. Commissie = €8.000. Eerste termijn: koper betaalt €25.000, verkoper ontvangt €17.000, Sense Lifting ontvangt €8.000. Overige termijnen: €25.000 volledig aan verkoper.',
        },
        { type: 'h3', text: 'Vereisten' },
        {
          type: 'list',
          items: [
            'Stripe Connect moet ingeschakeld zijn op het Sense Lifting Stripe-account',
            'De verkoper moet Stripe Express onboarding voltooien',
            'De eerste termijn moet minimaal groter zijn dan de 8% commissie',
          ],
        },
      ],
      en: [
        { type: 'h2', text: 'Ownership Transfer via Stripe' },
        {
          type: 'highlight',
          color: '#9B30FF',
          text: '⚠️ Only the current owner has access to the transfer module. Accessible via Admin → Transfer.',
        },
        { type: 'h3', text: 'Step 1 — Parties & Price' },
        {
          type: 'list',
          items: [
            'Search for the new owner(s) by name or email address',
            'Invite external parties by email if they don\'t have an account yet',
            'Set the total transfer price and currency',
            'Choose the number of installments (1/2/3/4/6/12) and payment frequency (monthly/quarterly)',
            'The 8% commission is automatically calculated on the total amount',
            'The first installment must be larger than the 8% commission',
          ],
        },
        { type: 'h3', text: 'Step 2 — Contract' },
        {
          type: 'list',
          items: [
            'The system automatically generates a transfer contract',
            'The contract contains all parties, the price, payment schedule and commission structure',
            'You can regenerate or print the contract',
            'Keep the contract for your own administration — it is not legally binding without signatures',
          ],
        },
        { type: 'h3', text: 'Step 3 — Stripe Payment' },
        {
          type: 'list',
          items: [
            'The seller goes through Stripe Express onboarding (one-time, ~5 min)',
            'Stripe verifies the seller\'s identity and bank details — Sense Lifting never sees this data',
            'The buyer pays each installment via Stripe Checkout (secure payment page)',
            'On the first payment, Sense Lifting automatically retains 8% of the total as commission',
            'The seller receives the remaining amount of the first installment minus the commission',
            'Remaining installments are fully paid out to the seller',
          ],
        },
        {
          type: 'highlight',
          color: '#4AC87A',
          text: '✅ After successful first payment, the ownership transfer is automatically registered in the system. The owner ID is updated and a journal entry is created.',
        },
        { type: 'h3', text: 'Commission Calculation' },
        {
          type: 'p',
          text: 'Commission = 8% × total transfer price. The commission is withheld once on the first installment. Example: Total price €100,000, 4 installments of €25,000. Commission = €8,000. First installment: buyer pays €25,000, seller receives €17,000, Sense Lifting receives €8,000. Remaining installments: €25,000 fully to seller.',
        },
        { type: 'h3', text: 'Requirements' },
        {
          type: 'list',
          items: [
            'Stripe Connect must be enabled on the Sense Lifting Stripe account',
            'The seller must complete Stripe Express onboarding',
            'The first installment must be at least greater than the 8% commission',
          ],
        },
      ],
    },
  },
  {
    id: 'privacy',
    icon: '🔒',
    nl: 'Privacy & AVG',
    en: 'Privacy & GDPR',
    content: {
      nl: [
        { type: 'h2', text: 'Privacy, AVG/GDPR & Gegevensbescherming' },
        { type: 'h3', text: 'Grondbeginselen' },
        {
          type: 'list',
          items: [
            '✅ Toestemming — medewerkers kiezen actief welke gezondheidsdata ze delen (opt-in)',
            '✅ Minimalisatie — we verzamelen alleen data die nodig is voor de aangeboden functionaliteit',
            '✅ Transparantie — medewerkers kunnen altijd inzien welke data gedeeld is',
            '✅ Anonimisering — pulse- en welzijnsdata wordt geaggregeerd vóór presentatie aan managers',
            '✅ Export — alle persoonsdata kan worden geëxporteerd via HR → GDPR Export',
          ],
        },
        { type: 'h3', text: 'Managers mogen NIET zien' },
        {
          type: 'list',
          items: [
            '❌ Individuele pulse-antwoorden',
            '❌ Gezondheidsdata van medewerkers die niet gedeeld hebben',
            '❌ Bankgegevens bij eigendomsoverdracht (Stripe beheert dit)',
            '❌ Privéberichten in de community',
          ],
        },
        { type: 'h3', text: 'Dataopslag' },
        {
          type: 'p',
          text: 'Alle data wordt opgeslagen op servers binnen de EU. Bankgegevens worden nooit door Sense Lifting opgeslagen — dit wordt beheerd door Stripe Inc. conform PCI-DSS Level 1 certificering.',
        },
      ],
      en: [
        { type: 'h2', text: 'Privacy, GDPR & Data Protection' },
        { type: 'h3', text: 'Fundamentals' },
        {
          type: 'list',
          items: [
            '✅ Consent — employees actively choose which health data to share (opt-in)',
            '✅ Minimization — we only collect data necessary for the offered functionality',
            '✅ Transparency — employees can always see what data has been shared',
            '✅ Anonymization — pulse and wellness data is aggregated before presentation to managers',
            '✅ Export — all personal data can be exported via HR → GDPR Export',
          ],
        },
        { type: 'h3', text: 'Managers may NOT see' },
        {
          type: 'list',
          items: [
            '❌ Individual pulse answers',
            '❌ Health data from employees who have not shared',
            '❌ Bank details during ownership transfer (Stripe manages this)',
            '❌ Private messages in the community',
          ],
        },
        { type: 'h3', text: 'Data storage' },
        {
          type: 'p',
          text: 'All data is stored on servers within the EU. Bank details are never stored by Sense Lifting — this is managed by Stripe Inc. in accordance with PCI-DSS Level 1 certification.',
        },
      ],
    },
  },
];

// ─── Content renderers ────────────────────────────────────────────────────────

function renderContent(blocks, lang) {
  return blocks.map((block, i) => {
    switch (block.type) {
      case 'h2':
        return (
          <h2 key={i} style={{ color: C.white, fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 14 }}>
            {block.text}
          </h2>
        );
      case 'h3':
        return (
          <h3 key={i} style={{ color: C.white, fontSize: 15, fontWeight: 700, marginTop: 20, marginBottom: 10 }}>
            {block.text}
          </h3>
        );
      case 'p':
        return (
          <p key={i} style={{ color: C.silver, fontSize: 13, lineHeight: 1.7, margin: '0 0 12px' }}>
            {block.text}
          </p>
        );
      case 'list':
        return (
          <ul key={i} style={{ listStyle: 'none', padding: 0, margin: '0 0 14px' }}>
            {block.items.map((item, j) => (
              <li key={j} style={{
                color: C.silver,
                fontSize: 13,
                lineHeight: 1.6,
                padding: '6px 0 6px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}>
                <span style={{ flexShrink: 0, marginTop: 2 }}></span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'highlight':
        return (
          <div key={i} style={{
            background: `${block.color}15`,
            border: `1px solid ${block.color}44`,
            borderLeft: `3px solid ${block.color}`,
            borderRadius: 10,
            padding: '12px 16px',
            color: block.color,
            fontSize: 13,
            lineHeight: 1.6,
            margin: '12px 0',
          }}>
            {block.text}
          </div>
        );
      case 'navTable':
        return (
          <div key={i} style={{ margin: '12px 0' }}>
            {block.rows.map((row, j) => (
              <div key={j} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 12px',
                background: '#1e1e28',
                borderRadius: 10,
                marginBottom: 6,
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{row.icon}</span>
                <div>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{row.name}</div>
                  <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5 }}>{row.desc}</div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'roleTable':
        return (
          <div key={i} style={{ margin: '12px 0' }}>
            {block.rows.map((row, j) => (
              <div key={j} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '10px 12px',
                background: '#1e1e28',
                borderRadius: 10,
                marginBottom: 6,
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{row.icon}</span>
                <div>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{row.role}</div>
                  <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5 }}>{row.desc}</div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'featureList':
        return (
          <div key={i} style={{ margin: '12px 0' }}>
            {block.items.map((item, j) => (
              <div key={j} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '14px',
                background: '#1e1e28',
                borderRadius: 12,
                marginBottom: 8,
                border: '1px solid rgba(255,255,255,0.07)',
              }}>
                <span style={{ fontSize: 22, flexShrink: 0, marginTop: 2 }}>{item.icon}</span>
                <div>
                  <div style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{item.title}</div>
                  <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'scientificBlock':
        return (
          <div key={i} style={{ margin: '12px 0' }}>
            {block.items.map((item, j) => (
              <div key={j} style={{
                padding: '12px 14px',
                background: '#1e1e28',
                borderRadius: 10,
                marginBottom: 8,
                border: '1px solid rgba(91,156,246,0.2)',
                borderLeft: '3px solid #5B9CF6',
              }}>
                <div style={{ color: '#5B9CF6', fontSize: 11, fontWeight: 600, marginBottom: 5 }}>{item.ref}</div>
                <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6 }}>{item.text}</div>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  });
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function BusinessManualScreen({ lang }) {
  const [activeSection, setActiveSection] = useState('intro');
  const isNl = lang === 'nl';

  const section = SECTIONS.find(s => s.id === activeSection);
  const content = section?.content?.[isNl ? 'nl' : 'en'] || [];

  return (
    <div style={{
      minHeight: '100%',
      background: _T.bg,
      fontFamily: 'Inter, system-ui, sans-serif',
      paddingBottom: 40,
    }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{
        padding: '16px 20px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22 }}>📖</span>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.white }}>
            {isNl ? 'Dashboard Handleiding' : 'Dashboard Manual'}
          </h2>
        </div>
        <p style={{ margin: 0, fontSize: 12, color: C.silver }}>
          {isNl
            ? 'Alles wat u moet weten over het Sense Lifting Business Dashboard'
            : 'Everything you need to know about the Sense Lifting Business Dashboard'
          }
        </p>
      </div>

      {/* Section nav — horizontal scroll pills */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: '12px 16px',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}>
        {SECTIONS.map(s => {
          const active = s.id === activeSection;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 14px',
                borderRadius: 20,
                border: active ? `1px solid ${_T.accent}` : '1px solid rgba(255,255,255,0.1)',
                background: active ? `${_T.accent}20` : 'transparent',
                color: active ? _T.accent : C.silver,
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                cursor: 'pointer',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              <span>{s.icon}</span>
              <span>{isNl ? s.nl : s.en}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{ padding: '20px 20px 0' }}>
        {renderContent(content, lang)}
      </div>

      {/* Footer nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 20px 0', gap: 10 }}>
        {(() => {
          const idx = SECTIONS.findIndex(s => s.id === activeSection);
          const prev = SECTIONS[idx - 1];
          const next = SECTIONS[idx + 1];
          return (
            <>
              {prev ? (
                <button
                  onClick={() => setActiveSection(prev.id)}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.04)',
                    color: C.silver,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  ← {prev.icon} {isNl ? prev.nl : prev.en}
                </button>
              ) : <div style={{ flex: 1 }} />}
              {next ? (
                <button
                  onClick={() => setActiveSection(next.id)}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: 12,
                    border: `1px solid ${_T.accent}33`,
                    background: `${_T.accent}15`,
                    color: _T.accent,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'right',
                  }}
                >
                  {next.icon} {isNl ? next.nl : next.en} →
                </button>
              ) : <div style={{ flex: 1 }} />}
            </>
          );
        })()}
      </div>
    </div>
  );
}
